import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { lastValueFrom, take } from 'rxjs'
import { CommonService } from '@appShared/services/common.service'
import {
   IShareholder,
   IShareholderReportGraph,
   IShareholderSummaryInfo
} from '@appShared/interfaces/[Model-based]'
import * as _ from 'lodash'

@Injectable({ providedIn: 'root' })
export class ShareholderService {
   /* https://blogs.msmvps.com/deborahk/build-a-simple-angular-service-to-share-data/ */
   private _sharehoderApi = '/api/portal/shareholders';

   constructor(
      private _http: HttpClient,
      private _commonService: CommonService
   ) { }

   async getShareholder(pin: string): Promise<IShareholder> {
      const url = `${this._sharehoderApi}/${pin}`

      const request$ = this._http.get<IShareholder>(url).pipe(take(1))

      return await lastValueFrom<IShareholder>(request$)
   }

   private async _getShareholderForReport(id: number): Promise<IShareholderReportGraph> {
      const url = `${this._sharehoderApi}/${id}/report`

      const request$ = this._http.get<IShareholderReportGraph>(url).pipe(take(1))

      return await lastValueFrom<IShareholderReportGraph>(request$)
   }

   generatePlanPdf(incomingShareHolder: IShareholder): void {

      if (incomingShareHolder?.id) {

         this._commonService.messageUser(
            `Creating report for ${incomingShareHolder.info?.displayName ?? ((incomingShareHolder as unknown) as IShareholderSummaryInfo).displayName}`,
            'Generating Report'
         )

         const formatCurrency = this._commonService.formatCurrency
         const formatNumber = this._commonService.formatNumber

         this._getShareholderForReport(incomingShareHolder.id)
            .then(shareholderReportGraph => {

               const shareholderReportGraphInfo = shareholderReportGraph.info
               const shareholder = shareholderReportGraph.shareholder
               const stockPlanDetails = shareholderReportGraph.stockPlanDetails
               const reportAsOf = `12/31/${new Date().getFullYear() - 1}`
               const repurchaseOptionAsOf = shareholder.repurchaseOptionAsOf
                  ? this._commonService.dateTime.formatDate(new Date(shareholder.repurchaseOptionAsOf))
                  : ''
               const shareholderNameDisplay = repurchaseOptionAsOf
                  ? `${shareholder.info.displayName} - Share Value locked as of: ${repurchaseOptionAsOf}`
                  : shareholder.info.displayName
               const contentHeader = [
                  {
                     text: [
                        {
                           text: 'Southwestern Family of Companies\n',
                           style: 'header'
                        },
                        {
                           text: `Summary of all Stock Purchases and Options\n`,
                           style: 'subheader'
                        },
                        {
                           text: `As of ${reportAsOf}\n`,
                           style: 'subSubheader'
                        }
                     ],
                     style: {
                        alignment: 'center'
                     }
                  },
                  {
                     columns: [
                        {
                           text: shareholderNameDisplay,
                           width: '50%',
                           fontSize: 12,
                           bold: true,
                           italics: true
                        },
                        {
                           text: '* see last page for column definitions',
                           width: '50%',
                           fontSize: 10,
                           alignment: 'right'
                        }
                     ]
                  }
               ]
               const reportTableRowArray = []

               /* loop for table row data */
               stockPlanDetails.forEach((stockPlanDetail, index) => {

                  const stockPlanDetailInfo = stockPlanDetail.info

                  //if(index === 0) return

                  reportTableRowArray.push([
                     {
                        text: stockPlanDetail.year,
                        style: 'rowDetail',
                        alignment: 'center'
                     },
                     /* shares offered */
                     {
                        text: formatNumber(stockPlanDetailInfo.adjustedShareOffer),
                        style: 'rowDetail',
                        alignment: 'center'
                     },
                     /* scheduled purchases shares */
                     {
                        text: formatNumber(stockPlanDetailInfo.scheduledPurchasedShares),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* scheduled purchases amount */
                     {
                        text: formatCurrency(stockPlanDetailInfo.scheduledPurchasedSharesAmount, '1.0-0'),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* cumulative purchases shares */
                     {
                        text: formatNumber(stockPlanDetailInfo.cumulativePurchasedShares),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* cumulative purchases amount */
                     {
                        text: formatCurrency(stockPlanDetailInfo.cumulativePurchasedSharesAmount, '1.0-0'),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* repurchased by co shares */
                     {
                        text: formatNumber(stockPlanDetailInfo.repurchasedShares),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* repurchased by co amount */
                     {
                        text: formatCurrency(stockPlanDetailInfo.repurchasedSharesAmount, '1.0-0'),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* dividends recieved current year */
                     {
                        text: formatCurrency(stockPlanDetailInfo.currentYearDividendAmountPaid, '1.0-0'),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* dividends recieved to date */
                     {
                        text: formatCurrency(stockPlanDetailInfo.toDateDividendsAmountPaid, '1.0-0'),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* new cash flow current year */
                     {
                        text: formatCurrency(stockPlanDetailInfo.currentYearNetCashflow, '1.0-0'),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* new cash flow to date */
                     {
                        text: formatCurrency(stockPlanDetailInfo.toDateNetCashflow, '1.0-0'),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     /* fair value */
                     {
                        text: stockPlanDetail.statedShareValue ? formatCurrency(stockPlanDetail.statedShareValue) : '',
                        style: 'rowDetail',
                        bold: stockPlanDetail.isRepurchaseOptionShareValue,
                        alignment: 'right'
                     },
                     /* stated value note */
                     {
                        text: stockPlanDetail.statedValueNote,
                        style: 'rowDetail'
                     },
                     /* dividends */
                     {
                        text: formatCurrency(stockPlanDetail.dividendPerShare),
                        style: 'rowDetail',
                        alignment: 'right'
                     },
                     {
                        text: stockPlanDetail.dividendNote,
                        style: 'rowDetail'
                     }
                  ])
               })

               /* shareholderReportGraphInfo totals */
               const evergreenRowTotals = {
                  purchasedShares: formatNumber(shareholderReportGraphInfo.cumulativeEvergreenShares),
                  amountPurchasedShares: formatCurrency(shareholderReportGraphInfo.cumulativeAmountEvergreenShares, '1.0-0'),
                  cumulativePurchasedShares: formatNumber(shareholderReportGraphInfo.cumulativeAllShares),
                  amountCumulativePurchasedShares: formatCurrency(shareholderReportGraphInfo.cumulativeAmountAllShares, '1.0-0'),
                  cumulativeRepurchasedShares: formatNumber(shareholderReportGraphInfo.cumulativeRepurchasedShares),
                  cumulativeAmountRepurchasedShares: formatCurrency(shareholderReportGraphInfo.cumulativeAmountRepurchasedShares, '1.0-0'),
                  cumulativeAmountDividendsPaid: formatCurrency(shareholderReportGraphInfo.cumulativeAmountDividendsPaid, '1.0-0'),
                  amountCashFlowCurrentYear: formatCurrency(shareholderReportGraphInfo.cumulativeAmountNetEvergreenCashFlow, '1.0-0'),
                  amountCashFlowToDate: formatCurrency(shareholderReportGraphInfo.cumulativeAmountNetFinalCashFlow, '1.0-0')
               }
               const footerTotals = {
                  totalBalanceOwed: formatCurrency(shareholderReportGraphInfo.cumulativeAmountAllShares, '1.0-0'),
                  paidToDate: formatCurrency(shareholderReportGraphInfo.paidPurchasedSharesAmount, '1.0-0'),
                  remainingOwed: formatCurrency(shareholderReportGraphInfo.remainingOwedPurchasedSharesAmount, '1.0-0'),
                  netPaidShares: formatNumber(shareholderReportGraphInfo.netPaidForShares),
                  installmentShares: formatNumber(shareholderReportGraphInfo.unpaidInstallmentShares),
                  excEvergreenOptions: formatNumber(shareholderReportGraphInfo.cumulativeEvergreenVestedShares),
                  evergreenOptionsNotExc: formatNumber(shareholderReportGraphInfo.cumulativeEvergreenRetainedShares),
                  totalNetSharesRepurchased: formatNumber(shareholderReportGraphInfo.cumulativeNetShares),
                  currentStatedShareValue: formatCurrency(shareholderReportGraphInfo.currentFairValue),
                  totalFairValue: formatCurrency(shareholderReportGraphInfo.totalFairValueOfAll),
                  totalFairValueExcEvg: formatCurrency(shareholderReportGraphInfo.totalFairNetValueOfAll)
               }

               const docDefinition = {
                  pageOrientation: 'landscape',
                  pageMargins: [10, 10, 10, 40],
                  // footer: function (currentPage, pageCount) { return currentPage.toString() + ' of ' + pageCount; },
                  footer(currentPage, pageCount): any {
                     return {
                        margin: 5,
                        columns: [
                           {
                              fontSize: 9,
                              text: [
                                 {
                                    text: `Page ${currentPage.toString()} of ${pageCount}`,
                                    bold: true
                                 }
                              ],
                              alignment: 'center'
                           }
                        ]
                     }

                  },
                  content: [
                     contentHeader,
                     {
                        style: 'reportTable',
                        table: {
                           headerRows: 3,
                           widths: [
                              '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*'
                           ],
                           body: [
                              /* column number header*/
                              [
                                 '',
                                 { text: 1, alignment: 'center' },
                                 { text: 2, alignment: 'right' },
                                 { text: 3, alignment: 'right' },
                                 { text: 4, alignment: 'right' },
                                 { text: 5, alignment: 'right' },
                                 { text: 6, alignment: 'right' },
                                 { text: 7, alignment: 'right' },
                                 { text: 8, alignment: 'right' },
                                 { text: 9, alignment: 'right' },
                                 { text: 10, alignment: 'right' },
                                 { text: 11, alignment: 'right' },
                                 { text: 12, colSpan: 2, alignment: 'center' },
                                 '',
                                 { text: 13, colSpan: 2, alignment: 'center' },
                                 ''
                              ],
                              /* header 1 */
                              [
                                 {
                                    text: 'Years',
                                    style: 'tableHeader',
                                    rowSpan: 2,
                                    alignment: 'center'
                                 },
                                 {
                                    text: 'Shares\nOffered',
                                    rowSpan: 2,
                                    style: 'tableHeader',
                                    alignment: 'center'
                                 },
                                 {
                                    text: 'Scheduled Purchases',
                                    style: 'tableHeader',
                                    colSpan: 2,
                                    alignment: 'right'
                                 },
                                 '',
                                 {
                                    text: 'Cumulative Purchases',
                                    style: 'tableHeader',
                                    colSpan: 2,
                                    alignment: 'right'
                                 },
                                 '',
                                 {
                                    text: 'Repurchased By CO',
                                    style: 'tableHeader',
                                    colSpan: 2,
                                    alignment: 'right'
                                 },
                                 '',
                                 {
                                    text: 'Dividends Received',
                                    style: 'tableHeader',
                                    colSpan: 2,
                                    alignment: 'right'
                                 },
                                 '',
                                 {
                                    text: 'NET Cash Flow',
                                    style: 'tableHeader',
                                    colSpan: 2,
                                    alignment: 'center'
                                 },
                                 '',
                                 {
                                    text: 'Fair Value\nHistory',
                                    rowSpan: 2,
                                    colSpan: 2,
                                    style: 'tableHeader',
                                    alignment: 'center'
                                 },
                                 '',
                                 {
                                    text: 'Dividend\nHistory',
                                    rowSpan: 2,
                                    colSpan: 2,
                                    style: 'tableHeader',
                                    alignment: 'center'
                                 },
                                 ''
                              ],
                              /* header 2 */
                              [
                                 '',
                                 '',
                                 {
                                    text: '#',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: '$',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: '#',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: '$',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: '#',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: '$',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: 'Curr Yr',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: 'To Date',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: 'Curr Yr',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 {
                                    text: 'To Date',
                                    style: 'tableHeader',
                                    alignment: 'right'
                                 },
                                 '',
                                 '',
                                 '',
                                 ''
                              ],

                              /* array of table data */
                              ...reportTableRowArray,

                              /* Evergreen table row */
                              [
                                 {
                                    text: 'Evergreens',
                                    bold: true,
                                    style: 'rowDetail',
                                    alignment: 'center'
                                 },
                                 '',
                                 /* scheduled purchases */
                                 {
                                    text: evergreenRowTotals.purchasedShares,
                                    style: 'rowDetail',
                                    alignment: 'right'
                                 },
                                 {
                                    text: evergreenRowTotals.amountPurchasedShares,
                                    style: 'rowDetail',
                                    alignment: 'right'
                                 },
                                 /* cumulative purchases */
                                 {
                                    text: evergreenRowTotals.cumulativePurchasedShares,
                                    style: 'rowDetail',
                                    alignment: 'right'
                                 },
                                 {
                                    text: evergreenRowTotals.amountCumulativePurchasedShares,
                                    style: 'rowDetail',
                                    alignment: 'right'
                                 },
                                 /* repurchased by co */
                                 '',
                                 '',
                                 /* dividends recieved */
                                 '',
                                 '',
                                 /* new cash flow */
                                 {
                                    text: evergreenRowTotals.amountCashFlowCurrentYear,
                                    style: 'rowDetail',
                                    alignment: 'right'
                                 },
                                 {
                                    text: evergreenRowTotals.amountCashFlowToDate,
                                    style: 'rowDetail',
                                    alignment: 'right'
                                 },
                                 /* fair value */
                                 '',
                                 '',
                                 /* dividends */
                                 '',
                                 ''
                              ],
                              /* Totals table row */
                              [
                              {
                                 text: 'Totals',
                                 bold: true,
                                 style: 'rowDetail',
                                 alignment: 'center'
                              },
                              '',
                              '',
                              '',
                              /* cumulative purchases */
                              {
                                 text: evergreenRowTotals.cumulativePurchasedShares,
                                 style: 'rowDetail',
                                 alignment: 'right'
                              },
                              {
                                 text: evergreenRowTotals.amountCumulativePurchasedShares,
                                 style: 'rowDetail',
                                 alignment: 'right'
                              },
                              /* repurchased by co */
                              {
                                 text: evergreenRowTotals.cumulativeRepurchasedShares,
                                 style: 'rowDetail',
                                 alignment: 'right'
                              },
                              {
                                 text: evergreenRowTotals.cumulativeAmountRepurchasedShares,
                                 style: 'rowDetail',
                                 alignment: 'right'
                              },
                              /* dividends recieved */
                              '',
                              {
                                 text: evergreenRowTotals.cumulativeAmountDividendsPaid,
                                 style: 'rowDetail',
                                 alignment: 'right'
                              },
                              /* new cash flow */
                              '',
                              {
                                 text: evergreenRowTotals.amountCashFlowToDate,
                                 style: 'rowDetail',
                                 alignment: 'right'
                              },
                              /* fair value */
                              '',
                              '',
                              /* dividends */
                              '',
                              ''
                              ]
                           ]
                        },
                        // layout: 'lightHorizontalLines'
                        layout: {
                           // code from lightHorizontalLines:
                           hLineWidth: (i, node) => {
                              if (i === 0 || i === node.table.body.length) {
                                 return 0
                              }
                              return (i === node.table.headerRows) ? 2 : 1
                           },
                           vLineWidth: (i) => {
                              return 0
                           },
                           hLineColor: (i) => {
                              var reportDataRowCount = reportTableRowArray.length
                              var tableRowCount = reportDataRowCount + 3/*headerRowCount*/
                              var emphasizedLines = [1, tableRowCount, tableRowCount + 1]
                              return emphasizedLines.includes(i) ? 'black' : '#aaa'
                           },
                           paddingLeft: (i) => {
                              return i === 0 ? 0 : 8
                           },
                           paddingRight: (i, node) => {
                              return (i === node.table.widths.length - 1) ? 0 : 8
                           },
                           // code for zebra style:
                           fillColor: (i, node) => {
                              return (i % 2 === 0) ? '#eeeeee' : null
                           }
                        }
                     },
                     {
                        style: 'footerTable',
                        table: {
                           widths: [200, 290, 290],
                           body: [
                              [
                                 {
                                    columns: [
                                       { width: '*', text: '' },
                                       {
                                          width: 'auto',
                                          table: {
                                             heights: ['auto', 'auto', 10, 'auto'],
                                             body: [
                                                [
                                                   {
                                                      text: 'TOTAL BALANCE OWED:',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.totalBalanceOwed,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   {
                                                      text: 'PAID TO DATE:',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.paidToDate,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   '',
                                                   '',
                                                ],
                                                [
                                                   {
                                                      text: 'REMAINING OWED:',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.remainingOwed,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   },
                                                ]
                                             ],
                                             alignment: 'center'
                                          },
                                          layout: 'noBorders'
                                       },
                                       { width: '*', text: '' },
                                    ]
                                 },
                                 {
                                    columns: [
                                       { width: '*', text: '' },
                                       {
                                          width: 'auto',
                                          table: {
                                             body: [
                                                [
                                                   {
                                                      text: 'NET PAID FOR SHARES (A):',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.netPaidShares,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   {
                                                      text: 'INSTALLMENT SHARES NOT PAID FOR (B):',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.installmentShares,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   {
                                                      text: 'EXERCISABLE EVERGREEN OPTIONS (C):',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.excEvergreenOptions,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   {
                                                      text: 'EVERGREEN OPTIONS NOT YET EXERCISABLE (D):',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.evergreenOptionsNotExc,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   {
                                                      text: 'TOTAL SHARES NET OF # REPURCHASED (E):',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.totalNetSharesRepurchased,
                                                      style: 'rowDetail',
                                                      alignment: 'right'
                                                   }
                                                ]
                                             ],
                                             alignment: 'center'
                                          },
                                          layout: 'noBorders'
                                       },
                                       { width: '*', text: '' },
                                    ]
                                 },
                                 {
                                    columns: [
                                       { width: '*', text: '' },
                                       {
                                          width: 'auto',
                                          table: {
                                             body: [
                                                [
                                                   {
                                                      text: 'CURRENT FAIR VALUE:',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.currentStatedShareValue,
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   {
                                                      text: 'TOTAL FAIR VALUE OF ALL OPTIONS AND SHARES:\n(E * CURRENT FAIR VALUE)',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.totalFairValue,
                                                      alignment: 'right'
                                                   }
                                                ],
                                                [
                                                   {
                                                      text: 'TOTAL FAIR VALUE OF NET PAID AND EXERCISABLE\nEVERGREENS:\n([A + C] * CURRENT FAIR VALUE)-(COST OF\nEXERCISABLE EVERGREENS)',
                                                      bold: true,
                                                      alignment: 'right'
                                                   },
                                                   {
                                                      text: footerTotals.totalFairValueExcEvg,
                                                      alignment: 'right'
                                                   }
                                                ]
                                             ],
                                             alignment: 'center'
                                          },
                                          layout: 'noBorders'
                                       },
                                       { width: '*', text: '' },
                                    ]
                                 }
                              ]
                           ]
                        },
                        layout: 'noBorders'
                     },
                     {
                        text: 'All shares reflect applicable stock splits',
                        bold: true,
                        fontSize: 12,
                     },
                     {
                        text: [
                           {
                              text: 'Southwestern / Great American, Inc.\n',
                              style: 'header'
                           },
                           {
                              text: `Stock Plan Column Definitions`,
                              style: 'subheader'
                           }
                        ],
                        style: {
                           alignment: 'center'
                        },
                        margin: [0, 40, 0, 20],
                        pageBreak: 'before'
                     },
                     {
                        table: {
                           widths: ['auto', '*'],
                           body: [
                              [
                                 { text: 'Column 1:', style: 'columnDefsHeader' },
                                 'Shares Offered # number of shares offered in the year earned'
                              ],
                              [
                                 { text: 'Column 2:', style: 'columnDefsHeader' },
                                 'Scheduled Purchases # - schedule of shares to be purchased in the year to be purchased'
                              ],
                              [
                                 { text: 'Column 3:', style: 'columnDefsHeader' },
                                 'Scheduled Purchases $ - schedule of cash investment in the year to be invested'
                              ],
                              [
                                 { text: 'Column 4:', style: 'columnDefsHeader' },
                                 'Cumulative Purchases # - cumulative number of Scheduled Purchases # (total of column 2)'
                              ],
                              [
                                 { text: 'Column 5:', style: 'columnDefsHeader' },
                                 'Cumulative Purchases $ - cumulative amount of Scheduled Purchases $ (total of column 3)'
                              ],
                              [
                                 { text: 'Column 6:', style: 'columnDefsHeader' },
                                 'Repurchased by Company # - number of shares repurchased by the company in the year of the transaction'
                              ],
                              [
                                 { text: 'Column 7:', style: 'columnDefsHeader' },
                                 'Repurchased by Company $ - proceeds received for shares repurchased by the company in the year of the'
                              ],
                              [
                                 { text: 'Column 8:', style: 'columnDefsHeader' },
                                 'Dividends Received Current Year # dividends and gross dividend equivalents received by year'
                              ],
                              [
                                 { text: 'Column 9:', style: 'columnDefsHeader' },
                                 'Dividends Received To Date # cumulative dividends and gross dividend equivalents received (total of column 8)'
                              ],
                              [
                                 { text: 'Column 10:', style: 'columnDefsHeader' },
                                 'Net Cash Flow Current Year # net cash flow (Invested) or Received from all stock transactions (Note: bracketed amount means cash investment; unbracketed amounts means positive cash flow) (Column 7 plus Column 8 minus Column 3)'
                              ],
                              [
                                 { text: 'Column 11:', style: 'columnDefsHeader' },
                                 'Net Cash Flow To Date # cumulative net cash flow (Invested) or Received from all stock transactions (Note: bracketed amount means cash investment; unbracketed amounts means positive cash flow) (total of column 10)'
                              ],
                              [
                                 { text: 'Column 12:', style: 'columnDefsHeader' },
                                 'History of Fair Value per Share (adjusted for stock splits in 1994 at 2-for-1, 2006 at 3-for-1, and 2012 at 2-for-1)'
                              ],
                              [
                                 { text: 'Column 13:', style: 'columnDefsHeader' },
                                 'History of Dividends per Share (adjusted for stock splits in 1994 at 2-for-1, 2006 at 3-for-1, and 2012 at 2-for-1)'
                              ]
                           ]
                        },
                        layout: 'noBorders',
                        margin: [20, 10, 0, 0],
                     }
                  ],
                  styles: {
                     header: {
                        fontSize: 14,
                        bold: true,
                        textAlign: 'center',
                        textTransform: 'uppercase'
                        // margin: [0, 0, 0, 10]
                     },
                     subheader: {
                        fontSize: 12,
                        textAlign: 'center'
                     },
                     subSubheader: {
                        fontSize: 10,
                        textAlign: 'center'
                     },
                     reportTable: {
                        margin: [0, 15, 0, 0]
                     },
                     tableHeader: {
                        bold: true,
                        fontSize: 8,
                        fillColor: '#eeeeee',
                        alignment: 'center'
                     },
                     reportHeader: {
                        bold: true,
                        fontSize: 13,
                        color: 'black'
                     },
                     right: {
                        textAlign: 'right'
                     },
                     rowDetail: {
                        fontSize: 8,
                        noWrap: true
                     },
                     columnDefsHeader: {
                        noWrap: true,
                        bold: true
                     },
                     footerTable: {
                        margin: [0, 15, 0, 0],
                        fontSize: 8
                     },
                  }
               }

               this._commonService.generatePdf(docDefinition)
            })
            .catch(() => {
               /* Do Nothing */
            })
      }
   }
}
